import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, UserProfile, AutofillTask, Document } from '@formmitra/shared';

interface AuthState {
  user: User | null;
  profile: UserProfile | null;
  token: string | null;
  setAuth: (user: User, token: string) => void;
  setProfile: (profile: UserProfile) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      profile: null,
      token: null,
      setAuth: (user, token) => set({ user, token }),
      setProfile: (profile) => set({ profile }),
      logout: () => set({ user: null, profile: null, token: null }),
    }),
    { name: 'formmitra-auth' }
  )
);

interface TaskState {
  currentTask: AutofillTask | null;
  setCurrentTask: (task: AutofillTask | null) => void;
  updateTask: (patch: Partial<AutofillTask>) => void;
}

export const useTaskStore = create<TaskState>((set) => ({
  currentTask: null,
  setCurrentTask: (currentTask) => set({ currentTask }),
  updateTask: (patch) =>
    set((s) => ({
      currentTask: s.currentTask ? { ...s.currentTask, ...patch } : null,
    })),
}));

interface DocState {
  documents: Document[];
  setDocuments: (docs: Document[]) => void;
  addDocument: (doc: Document) => void;
  removeDocument: (id: string) => void;
}

export const useDocStore = create<DocState>()(
  persist(
    (set) => ({
      documents: [],
      setDocuments: (documents) => set({ documents }),
      addDocument: (doc) =>
        set((s) => ({ documents: [...s.documents, doc] })),
      removeDocument: (id) =>
        set((s) => ({ documents: s.documents.filter((d) => d.id !== id) })),
    }),
    { name: 'formmitra-docs' }
  )
);
